import type { ApplicationInsights, ITelemetryItem } from "@microsoft/applicationinsights-web";

// Real-user metrics for comparing builds (application_Version): how long users wait for an app, how much code they
// download, and how much of it comes from the browser cache. Event definitions and queries: Osp\Docs\Customer-Metrics.

type Properties = Record<string, string>;
type Measurements = Record<string, number>;
type MetricsSink = (name: string, properties: Properties, measurements: Measurements) => void;
type VisitType = "First" | "NewVersion" | "Repeat";
type Delivery = "network" | "cache" | "revalidated";

const lastPageLoadStorageKey = "M365Pulse.LastPageLoad";
const maxQueuedEvents = 10;
const resourceTimingBufferSize = 1000;
const msPerHour = 60 * 60 * 1000;

// The default buffer (250 entries) can fill up before the first app is on screen, and the browser drops entries once it's full.
performance.setResourceTimingBufferSize(resourceTimingBufferSize);

const roundMs = (ms: number) => Math.round(ms);
const roundToTenth = (value: number) => Math.round(value * 10) / 10;
const toKiB = (bytes: number) => roundToTenth(bytes / 1024);
const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

const readValue = (source: unknown, key: string): unknown => (typeof source === "object" && source !== null ? Reflect.get(source, key) : undefined);

const readNumber = (source: unknown, key: string) => {
    const value = readValue(source, key);
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
};

const readString = (source: unknown, key: string) => {
    const value = readValue(source, key);
    return typeof value === "string" ? value : undefined;
};

// navigator.connection is only available in Chromium browsers (Edge, Chrome); other browsers report "unknown".
const getConnection = () => {
    const connection: unknown = Reflect.get(navigator, "connection");
    const measurements: Measurements = {};
    const rtt = readNumber(connection, "rtt");
    const downlink = readNumber(connection, "downlink");
    if (rtt !== undefined) {
        measurements.RttMs = rtt;
    }
    if (downlink !== undefined) {
        measurements.DownlinkMbps = downlink;
    }
    return {
        properties: { EffectiveType: readString(connection, "effectiveType") ?? "unknown", SaveData: String(readValue(connection, "saveData") === true) },
        measurements
    };
};

const readLastPageLoad = () => {
    try {
        const value: unknown = JSON.parse(window.localStorage.getItem(lastPageLoadStorageKey) ?? "null");
        const at = readNumber(value, "at");
        const version = readString(value, "version");
        return at !== undefined && version !== undefined ? { at, version } : undefined;
    } catch {
        // A missing or unreadable record means this load counts as a first visit; there is nothing else to recover.
        return undefined;
    }
};

const writeLastPageLoad = (version: string) => {
    try {
        window.localStorage.setItem(lastPageLoadStorageKey, JSON.stringify({ at: Date.now(), version }));
    } catch {
        // Without the record, the next load is counted as a first visit.
    }
};

// Read before this page load records itself, so it describes the browser cache this load started with.
const lastPageLoad = readLastPageLoad();

const getVisit = (version: string) => {
    const visitType: VisitType = !lastPageLoad ? "First" : lastPageLoad.version === version ? "Repeat" : "NewVersion";
    const measurements: Measurements = lastPageLoad ? { HoursSinceLastLoad: roundToTenth((Date.now() - lastPageLoad.at) / msPerHour) } : {};
    return { visitType, measurements };
};

const isCodeResource = (entry: PerformanceResourceTiming) => {
    if (!entry.name.startsWith(window.location.origin)) {
        return false;
    }
    const path = new URL(entry.name).pathname;
    return path.endsWith(".js") || path.endsWith(".css");
};

// A 304 only sends headers, so fewer bytes cross the network than the cached body holds.
const getDelivery = (entry: PerformanceResourceTiming): Delivery => {
    if (readNumber(entry, "responseStatus") === 304 || (entry.transferSize > 0 && entry.transferSize < entry.encodedBodySize)) {
        return "revalidated";
    }
    return entry.transferSize === 0 ? "cache" : "network";
};

/** Sums up this site's JS and CSS files whose requests started between `from` and `to` (ms since the page started loading). */
export const summarizeCodeDownloads = (entries: PerformanceResourceTiming[], from: number, to: number): Measurements => {
    const code = entries.filter((entry) => entry.startTime >= from && entry.startTime <= to && isCodeResource(entry));
    const fetched = code.filter((entry) => getDelivery(entry) !== "cache");
    const count = (delivery: Delivery) => code.filter((entry) => getDelivery(entry) === delivery).length;
    return {
        CodeFiles: code.length,
        CodeNetworkFiles: count("network"),
        CodeCachedFiles: count("cache"),
        CodeRevalidatedFiles: count("revalidated"),
        CodeTransferKiB: toKiB(sum(code.map((entry) => entry.transferSize))),
        CodeDecodedKiB: toKiB(sum(code.map((entry) => entry.decodedBodySize))),
        CodeDownloadMs: fetched.length
            ? roundMs(Math.max(...fetched.map((entry) => entry.responseEnd)) - Math.min(...fetched.map((entry) => entry.startTime)))
            : 0,
        CodeSlowestFileMs: fetched.length ? roundMs(Math.max(...fetched.map((entry) => entry.duration))) : 0
    };
};

const getResourceEntries = () => performance.getEntriesByType("resource").filter((entry) => entry instanceof PerformanceResourceTiming);

let lastHiddenAt = document.visibilityState === "hidden" ? 0 : -1;
document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
        lastHiddenAt = performance.now();
    }
});

// Background tabs render slowly, so their timings are flagged and left out of comparisons.
const wasHiddenSince = (from: number) => String(document.visibilityState === "hidden" || lastHiddenAt >= from);

let sink: MetricsSink | undefined;
let appVersion = "";
const queuedEvents: [string, Properties, Measurements][] = [];

const send = (name: string, properties: Properties, measurements: Measurements) => {
    if (sink) {
        sink(name, properties, measurements);
    } else if (queuedEvents.length < maxQueuedEvents) {
        queuedEvents.push([name, properties, measurements]);
    }
};

const getSegments = () => {
    const connection = getConnection();
    const visit = getVisit(appVersion);
    return {
        properties: { ...connection.properties, VisitType: visit.visitType },
        measurements: { ...connection.measurements, ...visit.measurements }
    };
};

let firstApp: { appId: string; readyAt: number } | undefined;
let currentAppId: string | undefined;
let pendingAppNavigation: { appId: string; start: number } | undefined;

const sendPageLoad = (appId: string, readyAt: number) => {
    const navigation = performance.getEntriesByType("navigation").find((entry) => entry instanceof PerformanceNavigationTiming);
    const segments = getSegments();
    const measurements: Measurements = {
        ...segments.measurements,
        ...summarizeCodeDownloads(getResourceEntries(), 0, readyAt),
        AppReadyMs: roundMs(readyAt)
    };
    if (navigation) {
        measurements.TtfbMs = roundMs(navigation.responseStart);
        measurements.DomContentLoadedMs = roundMs(navigation.domContentLoadedEventEnd);
        measurements.LoadEventMs = roundMs(navigation.loadEventEnd);
        measurements.DocumentTransferKiB = toKiB(navigation.transferSize);
    }
    send("PageLoad", { ...segments.properties, App: appId, NavigationType: navigation?.type ?? "unknown", WasHidden: wasHiddenSince(0) }, measurements);
    writeLastPageLoad(appVersion);
};

/** Called by the app route loader when a navigation to another app starts. */
export const markAppNavigationStart = (appId: string) => {
    pendingAppNavigation = { appId, start: performance.now() };
};

/** Called when an app's root component has mounted: its code has loaded and the app is on screen. */
export const markAppReady = (appId: string) => {
    const readyAt = performance.now();
    const fromAppId = currentAppId;
    const pending = pendingAppNavigation;
    currentAppId = appId;
    pendingAppNavigation = undefined;

    if (!firstApp) {
        firstApp = { appId, readyAt };
        const sendAfterLoad = () => setTimeout(() => sendPageLoad(appId, readyAt), 0);
        if (document.readyState === "complete") {
            sendAfterLoad();
        } else {
            window.addEventListener("load", sendAfterLoad, { once: true });
        }
        return;
    }
    if (pending?.appId !== appId) {
        return;
    }
    const segments = getSegments();
    send(
        "AppCodeLoad",
        { ...segments.properties, App: appId, FromApp: fromAppId ?? "", WasHidden: wasHiddenSince(pending.start) },
        { ...segments.measurements, ...summarizeCodeDownloads(getResourceEntries(), pending.start, readyAt), WaitMs: roundMs(readyAt - pending.start) }
    );
};

// Lets Web Vitals be split by connection and by cold or warm cache, like the events above.
const addSegmentsToWebVitals = (envelope: ITelemetryItem) => {
    if (envelope.baseType === "MetricData" && readString(envelope.baseData, "name")?.startsWith("WebVitals.")) {
        envelope.data = { ...envelope.data, ...getSegments().properties };
    }
};

export const startCustomerMetrics = (appInsights: ApplicationInsights) => {
    appVersion = readString(appInsights.context.application, "ver") ?? "";
    appInsights.addTelemetryInitializer(addSegmentsToWebVitals);
    sink = (name, properties, measurements) => appInsights.trackEvent({ name, properties, measurements });
    queuedEvents.splice(0).forEach(([name, properties, measurements]) => send(name, properties, measurements));
};
